import {
  ProductTier,
  PurchaseableSubTier,
  PurchaseableTier,
} from '@sparemin/api-sdk';
import React from 'react';

import { useDispatch } from 'react-redux';
import { OnUpgradeClick, SubscriptionPeriod } from 'blocks/Pricing/types';
import { onClickUpgradePlan } from 'redux/modules/mixpanel';
import {
  UpgradePlanLocation,
  UpgradePlanSource,
} from 'redux/modules/mixpanel/types';
import { usePlanSelector } from './PlanSelectorContext';

interface UnderlyingProps {
  period: SubscriptionPeriod;
  onUpgradeClick: OnUpgradeClick;
}

export interface InjectedWithPlanSelectorProps {
  location: UpgradePlanLocation;
  onUpgradeClick: (
    tier: PurchaseableTier,
    period: SubscriptionPeriod,
    subtier?: PurchaseableSubTier,
  ) => void;
}

export default function withPlanSelector(
  tier: ProductTier,
  source?: UpgradePlanSource,
) {
  return <P extends {}>(
    Plan: React.ComponentType<P & UnderlyingProps>,
  ): React.FC<
    Omit<P, keyof UnderlyingProps> & InjectedWithPlanSelectorProps
  > => {
    const WrappedComponent = props => {
      const { location, onUpgradeClick, ...restProps } = props;
      const dispatch = useDispatch();
      const { period } = usePlanSelector();

      return (
        <Plan
          onUpgradeClick={({ subtier }) => {
            dispatch(onClickUpgradePlan(source, location, period));
            onUpgradeClick(tier, period, subtier);
          }}
          period={period}
          {...restProps}
        />
      );
    };

    WrappedComponent.displayName = `WithPlanSelector(${Plan.displayName})`;

    return WrappedComponent;
  };
}
